"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.PathBasedPainter = void 0;
var Painter_1 = require("./Painter");
var SvgRenderingContext_1 = require("../rendering/SvgRenderingContext");
/**
 * The recommended base class for painters. Just implement {@link tracePath} and
 * strokes/shadows should work correctly out of the box.
 */
var PathBasedPainter = /** @class */ (function (_super) {
    __extends(PathBasedPainter, _super);
    function PathBasedPainter() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._strokeMiterLimit = 200;
        return _this;
    }
    /**
     * Clips subsequent draw operations to the object's outline.
     */
    PathBasedPainter.prototype.clipPath = function (ctx, store, object) {
        this.tracePath(ctx, store, object);
        ctx.clip();
    };
    PathBasedPainter.prototype.paintStroke = function (store, object, state) {
        // Note: when you apply a shadow to a `ctx.stroke()` operation, you don't get a full
        // rectangular shadow, but rather a shadow outline. To illustrate:
        //
        // Not this:        But this:
        //
        // :|::::::|:       :|:    :|:
        // :|::::::|:       :|:    :|:
        // :|::::::|:       :|:    :|:
        //
        // "|" = stroke, ":" = shadow
        //
        // For most objects, the hollow area gets covered up entirely when the object's fill
        // is painted, so there's no problem. The one exception is objects with an offset
        // shadow. Those need special rendering logic.
        if (this.hasOffsetShadow(object)) {
            this._paintStrokeViaSublayer(store, object, state);
        }
        else {
            this._paintStrokeDirectly(store, object, state);
        }
    };
    /**
     * Paints *and fills* the stroke on a separate sub-canvas layer, then applies the shadow
     * while painting that layer onto the result. Because the stroke was filled, the object
     * receives a full shadow instead of a "hollow" one.
     */
    PathBasedPainter.prototype._paintStrokeViaSublayer = function (store, object, state) {
        var ctx = store.layers
            .child("PathBasedPainter/stroke-pre-shadow")
            .clear().ctx;
        ctx.save();
        this.translatePastSpacing(ctx, object);
        this.applyStrokeSettings(ctx, object);
        this.translateForCrispLine(ctx);
        this.tracePath(ctx, store, object);
        ctx.miterLimit = this._strokeMiterLimit;
        ctx.fillStyle = object.strokeStyle;
        if (this.useLineJoinRoundWhenApplyingStroke(object)) {
            ctx.lineJoin = "round";
        }
        ctx.stroke();
        ctx.fill();
        ctx.restore();
        this.applyShadowSettings(store.layers.result.ctx, object);
        store.layers.composite("PathBasedPainter/stroke-pre-shadow", store.layers.result.ctx);
        this.removeShadowSettings(store.layers.result.ctx);
        this._eraseExcessStroke(ctx, store, object, state);
    };
    /**
     * Paints a stroke directly onto the result layer.
     */
    PathBasedPainter.prototype._paintStrokeDirectly = function (store, object, state) {
        var ctx = store.layers.result.ctx;
        ctx.save();
        this.translatePastSpacing(ctx, object);
        this.applyStrokeSettings(ctx, object);
        this.applyShadowSettings(ctx, object);
        this.translateForCrispLine(ctx);
        this.tracePath(ctx, store, object);
        ctx.miterLimit = this._strokeMiterLimit;
        if (this.useLineJoinRoundWhenApplyingStroke(object)) {
            ctx.lineJoin = "round";
        }
        ctx.stroke();
        ctx.restore();
        this._eraseExcessStroke(ctx, store, object, state);
    };
    /**
     * Erases the part of the stroke that should be behind the fill. In most scenarios that part
     * would be obscured anyway, but when the fill has transparency, it would be visible.
     */
    PathBasedPainter.prototype._eraseExcessStroke = function (ctx, store, object, state) {
        if (state.ctx instanceof SvgRenderingContext_1.SvgRenderingContext) {
            throw new Error("Stroke in SVG rendering is currently not supported, "
                + "because it renders as a black rectangle in the SVG-to-PDF step.");
        }
        ctx.save();
        this.translatePastSpacing(ctx, object);
        this.tracePath(ctx, store, object);
        ctx.globalCompositeOperation = "destination-out";
        ctx.fill();
        ctx.restore();
    };
    /**
     * Override this to prevent miter overshoots when applying a stroke.
     */
    PathBasedPainter.prototype.useLineJoinRoundWhenApplyingStroke = function (object) {
        return false;
    };
    /**
     * Override this to enable extra painting space for stroke miters.
     */
    PathBasedPainter.prototype.needsMiterOvershoot = function (object) {
        return false;
    };
    /**
     * Estimates extra horizontal painting space needed for stroke miters.
     */
    PathBasedPainter.prototype.horizontalMiterOvershootOf = function (object) {
        if (this.needsMiterOvershoot(object)) {
            var ratio = this.scaledBaseWidthOf(object) / this.scaledBaseHeightOf(object);
            var strokeOvershoot = ratio * this.strokeSizeOf(object);
            return _super.prototype.horizontalSpacingOf.call(this, object) + 2 * strokeOvershoot;
        }
        else {
            return 0;
        }
    };
    /**
     * Estimates extra vertical painting space needed for stroke miters.
     */
    PathBasedPainter.prototype.verticalMiterOvershootOf = function (object) {
        if (this.needsMiterOvershoot(object)) {
            var ratio = this.scaledBaseHeightOf(object) / this.scaledBaseWidthOf(object);
            var strokeOvershoot = ratio * this.strokeSizeOf(object);
            return _super.prototype.verticalSpacingOf.call(this, object) + 2 * strokeOvershoot;
        }
        else {
            return 0;
        }
    };
    /**
     * Override to add extra space for mitered strokes.
     */
    PathBasedPainter.prototype.horizontalSpacingOf = function (object) {
        return _super.prototype.horizontalSpacingOf.call(this, object) + this.horizontalMiterOvershootOf(object);
    };
    /**
     * Override to add extra space for mitered strokes.
     */
    PathBasedPainter.prototype.verticalSpacingOf = function (object) {
        return _super.prototype.verticalSpacingOf.call(this, object) + this.verticalMiterOvershootOf(object);
    };
    /**
     * Override to add extra space for mitered strokes.
     */
    PathBasedPainter.prototype.paintOffsetXOf = function (object) {
        return _super.prototype.paintOffsetXOf.call(this, object) - this.horizontalMiterOvershootOf(object);
    };
    /**
     * Override to add extra space for mitered strokes.
     */
    PathBasedPainter.prototype.paintOffsetYOf = function (object) {
        return _super.prototype.paintOffsetYOf.call(this, object) - this.verticalMiterOvershootOf(object);
    };
    return PathBasedPainter;
}(Painter_1.Painter));
exports.PathBasedPainter = PathBasedPainter;
//# sourceMappingURL=PathBasedPainter.js.map